import { CanceledError } from 'axios'

function restoreError(value = {}) {
  const types = { Error, TypeError, RangeError, SyntaxError, ReferenceError, URIError, EvalError }
  const ErrorType = Object.prototype.hasOwnProperty.call(types, value.name) ? types[value.name] : Error
  const error = new ErrorType(value.message || '请求加密失败')
  error.name = value.name || 'Error'
  if (value.code) error.code = value.code
  if (value.stack) error.stack = value.stack
  return error
}

/** 一个常驻 Worker；取消只读排队任务不会中断其他请求。 */
export function createHttpCryptoService({
  createWorker = () => typeof Worker === 'undefined' ? null : new Worker(new URL('./httpCrypto.worker.js', import.meta.url)),
  loadFallback = () => import('./httpCrypto.core.mjs'),
  startupTimeoutMs = 3000,
} = {}) {
  let worker = null, state = 'idle', startupTimer = null, active = null, revision = 0, fallbackPromise = null
  const queue = []
  const clearStartup = () => { clearTimeout(startupTimer); startupTimer = null }
  const removeAbort = (job) => job.signal?.removeEventListener('abort', job.onAbort)
  const settle = (job, error, value) => {
    removeAbort(job)
    if (job.settled) return
    job.settled = true
    if (error) job.reject(error)
    else job.resolve(value)
  }
  const useFallback = () => {
    if (state === 'closed' || state === 'fallback') return
    clearStartup()
    const previous = worker
    worker = null
    previous?.terminate()
    state = 'fallback'
    if (active) {
      if (!active.settled) queue.unshift(active)
      active = null
    }
    pump()
  }
  const beginWorker = () => {
    state = 'starting'
    try {
      worker = createWorker()
      if (!worker) { useFallback(); return }
      const currentWorker = worker
      worker.onmessage = ({ data }) => {
        if (worker !== currentWorker || state === 'closed') return
        if (data?.type === 'ready') { clearStartup(); state = 'worker'; pump(); return }
        if (data?.type === 'startup-error') { useFallback(); return }
        if (!active || data?.id !== active.id) return
        const job = active
        active = null
        settle(job, data.type === 'error' ? restoreError(data.error) : null, data.result)
        pump()
      }
      worker.onerror = useFallback
      worker.onmessageerror = useFallback
      startupTimer = setTimeout(useFallback, startupTimeoutMs)
    } catch { useFallback() }
  }
  function pump() {
    if (active || state === 'closed' || state === 'starting') return
    if (state === 'idle') { if (queue.length) beginWorker(); return }
    while (queue[0]?.settled) queue.shift()
    const job = queue.shift()
    if (!job) return
    active = job
    if (state === 'worker') {
      try { worker.postMessage({ type: 'prepare', id: job.id, input: job.input }) }
      catch { useFallback() }
      return
    }
    fallbackPromise ||= Promise.resolve().then(loadFallback)
    fallbackPromise.then((core) => {
      if (job.settled || state === 'closed') return
      const prepare = typeof core === 'function' ? core : core.prepareHttpCrypto
      return prepare(job.input)
    }).then((result) => settle(job, null, result), (error) => settle(job, error)).finally(() => {
      if (active === job) active = null
      pump()
    })
  }
  return {
    prepare(input, { signal } = {}) {
      if (signal?.aborted || state === 'closed') return Promise.reject(new CanceledError())
      return new Promise((resolve, reject) => {
        const job = { id: ++revision, input, signal, resolve, reject, settled: false, onAbort: null }
        job.onAbort = () => {
          const index = queue.indexOf(job)
          if (index >= 0) queue.splice(index, 1)
          settle(job, new CanceledError())
        }
        signal?.addEventListener('abort', job.onAbort, { once: true })
        queue.push(job)
        pump()
      })
    },
    dispose() {
      state = 'closed'
      clearStartup()
      worker?.terminate()
      worker = null
      if (active) settle(active, new CanceledError())
      active = null
      queue.splice(0).forEach((job) => settle(job, new CanceledError()))
    },
  }
}

const httpCryptoService = createHttpCryptoService()
export const prepareHttpRequestCrypto = (input, options) => httpCryptoService.prepare(input, options)
